import type { TradeByStatus } from '@/hooks/use-trades';
import type { Trade } from '@/models/registry';
import { TRADE_STATUSES, type TradeStatus } from '@/models/types';
import { useMemo } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { STATUS_OPTIONS_MAP } from '../status/trade-status-selector';
import TradeInAppraisalCard from './trade-in-appraisal-card';

const VirtualizedTradeInAppraisalBoard = ({
  tradesByStatus,
  enableScope,
}: {
  tradesByStatus: TradeByStatus;
  enableScope: (scope: string) => void;
}) => {
  const columnMapForVirtualization = useMemo(() => {
    const columnMap: Record<
      number,
      {
        statusData:
          | {
              status: TradeStatus;
              count: number;
              trades: Trade[];
            }
          | undefined;
        trades: Trade[];
        count: number;
      }
    > = {};
    let index = 0;

    TRADE_STATUSES.forEach((status) => {
      const statusData = tradesByStatus?.[status];
      const trades = statusData?.trades;
      // .filter(filterCallback)
      // .filter(findInViewFilterCallback) || [];
      const count = trades?.length || 0;

      if (count == 0) {
        return;
      }

      columnMap[index] = {
        statusData: {
          status,
          count,
          trades,
        },
        trades,
        count,
      };
      index++;
    });

    return columnMap;
  }, [tradesByStatus]);

  const Column = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const { statusData, trades, count } = columnMapForVirtualization[index];
    const status = statusData?.status;

    const Row = ({
      index,
      style,
    }: {
      index: number;
      style: React.CSSProperties;
    }) => {
      const trade = trades[index];
      const hotkeyScope = `${trade.short_id}-trade-line-or-card`;

      return (
        <div
          style={style}
          key={trade.short_id}
          onMouseEnter={() => {
            enableScope(hotkeyScope);
          }}
          onMouseLeave={() => {
            enableScope('');
          }}
          className="overflow-x-hidden"
        >
          <TradeInAppraisalCard trade={trade} />
        </div>
      );
    };

    return (
      <div
        key={index}
        className="flex flex-col p-2 rounded-md bg-gray-100"
        style={style}
      >
        <div className="flex flex-row items-center gap-2 text-md  px-2 py-3 text-nowrap">
          <div className="size-4">
            {STATUS_OPTIONS_MAP.find((s) => s.value === status)?.icon}
          </div>

          <div className="text-md">
            {STATUS_OPTIONS_MAP.find((s) => s.value === status)?.label}
          </div>

          <div className="text-sm text-gray-500">{count}</div>
        </div>
        <div className="flex flex-col gap-2 overflow-y-auto h-screen">
          <AutoSizer>
            {({ height, width }) => {
              return (
                <FixedSizeList
                  height={height}
                  width={width}
                  itemCount={trades.length}
                  // 10 for the margin
                  itemSize={120.5 + 10}
                  layout="vertical"
                >
                  {Row}
                </FixedSizeList>
              );
            }}
          </AutoSizer>
        </div>
      </div>
    );
  };

  return (
    <div className="wrapper-statuses h-screen">
      <AutoSizer>
        {({ height, width }) => {
          return (
            <FixedSizeList
              className="List"
              height={height}
              itemCount={Object.keys(columnMapForVirtualization).length}
              itemSize={400}
              width={width}
              layout="horizontal"
            >
              {Column}
            </FixedSizeList>
          );
        }}
      </AutoSizer>
    </div>
  );
};

export default VirtualizedTradeInAppraisalBoard;
